import { customAlphabet } from "nanoid";
import { WecomContactType, ServiceType, SendContentType } from "../..";
import {
  WecomSendBusinessCardParams,
  WecomSendFileParams,
  WecomSendGifParams,
  WecomSendImageParams,
  WecomSendLinkParams,
  WecomSendMiniProgramParams,
  WecomSendTextMixedParams,
  WecomSendTextParams,
  WecomSendVideoNumParams,
  WecomSendVideoParams,
} from "../../services/wecom-command";
import Command from "../../services";

const urlAlphabet =
  "useandom-26T198340PX75pxJACKVERYMINDBUSHWOLFGQZbfghjklqvwyzrict";
/** 自定义nanoid字符串集，去掉默认字符串集合中的_ */
const nanoid = customAlphabet(urlAlphabet);

export type WecomTextSendData = {
  content: string;
};

export type WecomTextMixedSendData = {
  content: {
    subtype: number;
    text: string;
    nickname?: string;
  }[];
};
type UrlBaseSendData = {
  path: string;
  file_name: string;
};

export type WecomImageSendData = UrlBaseSendData;
export type WecomFileSendData = UrlBaseSendData;
export type WecomVideoSendData = UrlBaseSendData;
export type WecomGifSendData = UrlBaseSendData;
export type WecomLinkSendData = {
  desc: string;
  thumburl: string;
  title: string;
  url: string;
};
export type WecomBusinessCardSendData = {
  shared_id: string;
};

export type WecomMiniProgramSendData = {
  appid: string;
  cover_file_aeskey: string;
  cover_file_id: string;
  cover_file_md5: string;
  cover_file_size: number;
  desc: string;
  pagepath: string;
  thumburl: string;
  title: string;
  username: string;
};

export type WecomVideoNumSendData = {
  username: string;
  channel_name: string;
  channel_url: string;
  head_img_url: string;
  thumb_url: string;
  encode_data: string;
};

export type WecomVoiceSendData = {
  url: string;
  voice_length: number;
};

export type WecomSendDataType = {
  type: SendContentType;
  send_data:
    | WecomTextSendData
    | WecomTextMixedSendData
    | WecomImageSendData
    | WecomFileSendData
    | WecomVideoSendData
    | WecomGifSendData
    | WecomLinkSendData
    | WecomBusinessCardSendData
    | WecomMiniProgramSendData
    | WecomVideoNumSendData;
};

type BaseSendParams =
  | WecomSendTextParams
  | WecomSendTextMixedParams
  | WecomSendImageParams
  | WecomSendGifParams
  | WecomSendFileParams
  | WecomSendVideoParams
  | WecomSendLinkParams
  | WecomSendMiniProgramParams
  | WecomSendBusinessCardParams
  | WecomSendVideoNumParams;

class BaseSend<T extends WecomSendDataType, K extends BaseSendParams> {
  constructor(
    /** 发送消息的指令方法 */
    public sendMethod: (
      from_user_id: string,
      service_type: ServiceType,
      data: K
    ) => void,
    /** 存放实例化BaseSend的时候，传进来的send_params，这个数据最后会被放在db里面，用于消息发送失败之后，重新发送的逻辑 */
    public sendMessageParams: SendWecomMessageParams<T>
  ) {}
  /** BaseSend实例调用emit的时候发送指令并且返回一些参数，返回的参数构成如下
   *
   * request_key：用于判断消息是否发送成功，消息发送成功hook会返回15000或者15001，同时会返回与发送时相同的request_key
   *
   */
  async emit(params: {
    /**
     * data里面的type用来区分是要发什么类型的消息，send_data是严格按照接口文档写的发送消息的参数
     */
    data: T;
    from_user_id: string;
    service_type: ServiceType;
    to_id: string;
    chat_type: string;
  }): Promise<Omit<SendWecomMessageParams<T>, "service_type">> {
    const request_key = nanoid();
    const { data, from_user_id, service_type, to_id, chat_type } = params;
    const { send_data } = data;
    // sendParams是最后发送给api的参数
    const sendParams = {
      ...send_data,
      to_id,
      chat_type,
      // 一般能从this.sendMessageParams?.request_key获取到数据的，是第一次发送失败之后，重新发送才能拿到
      request_key: this.sendMessageParams?.request_key || request_key,
    } as K;
    this.sendMethod!(from_user_id, service_type, sendParams);
    this.sendMessageParams = {
      ...this.sendMessageParams,
      request_key,
    };
    Reflect.deleteProperty(this.sendMessageParams, "service_type");
    return Promise.resolve(this.sendMessageParams);
  }
}
export type SendWecomMessageParams<T> = {
  //  输入框的数据
  data: T;
  from_user_id: string;
  service_type: ServiceType;
  chat_id: string;
  to_id: string;
  chat_type: WecomContactType;
  request_key?: string;
};
export const sendWecomMessage = async (
  params: SendWecomMessageParams<WecomSendDataType>
): Promise<Omit<SendWecomMessageParams<WecomSendDataType>, "service_type">> => {
  const { data, from_user_id, service_type, to_id, chat_type } = params;
  const { type } = data;
  switch (type) {
    case SendContentType.TEXT: {
      const sendText = new BaseSend(
        Command.WecomCommand.sendTextCommand,
        params
      );
      return await sendText.emit({
        data,
        from_user_id,
        service_type,
        to_id,
        chat_type,
      });
    }
    case SendContentType.MENTION: {
      const sendTextMixed = new BaseSend(
        Command.WecomCommand.sendTextMixedCommand,
        params
      );
      return await sendTextMixed.emit({
        data,
        from_user_id,
        service_type,
        to_id,
        chat_type,
      });
    }
    case SendContentType.IMAGE: {
      const sendImage = new BaseSend(
        Command.WecomCommand.sendImageCommand,
        params
      );
      return await sendImage.emit({
        data,
        from_user_id,
        service_type,
        to_id,
        chat_type,
      });
    }
    case SendContentType.VIDEO: {
      const sendVideo = new BaseSend(
        Command.WecomCommand.sendVideoCommand,
        params
      );
      return await sendVideo.emit({
        data,
        from_user_id,
        service_type,
        to_id,
        chat_type,
      });
    }
    case SendContentType.FILE: {
      const sendFile = new BaseSend(
        Command.WecomCommand.sendFileCommand,
        params
      );
      return await sendFile.emit({
        data,
        from_user_id,
        service_type,
        to_id,
        chat_type,
      });
    }
    case SendContentType.GIF: {
      const sendGif = new BaseSend(Command.WecomCommand.sendGifCommand, params);
      return await sendGif.emit({
        data,
        from_user_id,
        service_type,
        to_id,
        chat_type,
      });
    }
    case SendContentType.LINK: {
      const sendLink = new BaseSend(
        Command.WecomCommand.sendLinkCommand,
        params
      );
      return await sendLink.emit({
        data,
        from_user_id,
        service_type,
        to_id,
        chat_type,
      });
    }
    case SendContentType.MINI_PROGRAM: {
      const sendMiniProgram = new BaseSend(
        Command.WecomCommand.sendMiniProgramCommand,
        params
      );
      return await sendMiniProgram.emit({
        data,
        from_user_id,
        service_type,
        to_id,
        chat_type,
      });
    }
    case SendContentType.BUSINESS_CARD: {
      const sendBusinessCard = new BaseSend(
        Command.WecomCommand.sendBusinessCardCommand,
        params
      );
      return await sendBusinessCard.emit({
        data,
        from_user_id,
        service_type,
        to_id,
        chat_type,
      });
    }
    case SendContentType.VIDEO_NUM: {
      const sendVideoNum = new BaseSend(
        Command.WecomCommand.sendVideoNumCommand,
        params
      );
      return await sendVideoNum.emit({
        data,
        from_user_id,
        service_type,
        to_id,
        chat_type,
      });
    }
  }
};
